/**
 * Price Breakup Sync - Simple and Clean Implementation
 * Syncs Gold Price Editor metafields with Product Details and Price Breakup tabs
 *
 * DATA SOURCES:
 * 1. Gold Price Editor Plugin - Variant Metafields (DI-GoldPrice.variant_config)
 *    - Purity, metal weight, making charges, diamond charges, misc charges, tax
 * 2. Gold Price Editor Plugin - Metal Prices (DI-GoldPrice.metal_prices)
 *    - Gold rates per gram for 14K, 18K, 21K, 22K, 24K
 * 3. Shopify Product Variants (Color, Size, etc.)
 *    - Metal color from variant options (accessed via DOM, NOT metafields)
 *
 * DEBUG MODE:
 * Set DEBUG_MODE = true to enable console logging and DEBUG section visibility
 * Set DEBUG_MODE = false for production (hides DEBUG section and console logs)
 */

(function() {
  'use strict';

  // ========================================
  // DEBUG FLAG - Change this to enable/disable debug features
  // ========================================
  const DEBUG_MODE = false; // Set to true to show DEBUG section and console logs

  class PriceBreakupSync {
    constructor() {
      if (DEBUG_MODE) console.log('💎 Price Breakup Sync: Initializing...');

      this.variantMetafields = window.variantMetafields || {};
      this.shopMetafields = window.shopMetafields || {};
      this.currentVariantId = null;
      this.optionSelectorsDisabled = false;
      this.updateTimeout = null; // For debouncing updates

      // Wait for DOM and metafields to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        this.init();
      }
    }

    init() {
      // Check if metafields are parsed
      if (!this.shopMetafields.parsed) {
        if (DEBUG_MODE) console.warn('Price Breakup Sync: Waiting for metafields to parse...');
        setTimeout(() => this.init(), 100);
        return;
      }

      if (DEBUG_MODE) console.log('Price Breakup Sync: Metafields ready');
      if (DEBUG_MODE) console.log('Available variant IDs:', Object.keys(this.variantMetafields));
      if (DEBUG_MODE) console.log('Shop data:', this.shopMetafields.parsed);

      // STEP 1: Get initial variant ID from the variant selector
      this.currentVariantId = this.getInitialVariantId();

      if (this.currentVariantId) {
        if (DEBUG_MODE) console.log('Price Breakup Sync: Initial variant ID:', this.currentVariantId);
        this.updateDisplay(this.currentVariantId);
      } else {
        if (DEBUG_MODE) console.warn('Price Breakup Sync: No initial variant found');
      }

      // STEP 2: Listen to variant changes
      this.setupVariantChangeListener();

      if (DEBUG_MODE) console.log('Price Breakup Sync: Initialization complete ✓');
    }

    /**
     * Get the initially selected variant ID from the variant selector
     */
    getInitialVariantId() {
      // The variant selector is in product-form.liquid line 111-129
      const variantSelect = document.querySelector('select[data-variant-select]');

      if (variantSelect) {
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        const variantId = selectedOption ? selectedOption.value : null;
        if (DEBUG_MODE) console.log('Found initial variant from select:', variantId);
        return variantId;
      }

      if (DEBUG_MODE) console.warn('Variant selector not found');
      return null;
    }

    /**
     * Set up listener for variant changes
     * The theme fires 'product:variant-change' event in theme.js line 6803
     */
    setupVariantChangeListener() {
      // Method 1: Listen to theme's custom event
      document.addEventListener('product:variant-change', (event) => {
        if (DEBUG_MODE) console.log('🔄 Price Breakup Sync: Variant changed event received', event.detail);

        if (event.detail && event.detail.variant) {
          const newVariantId = event.detail.variant.id.toString();
          if (DEBUG_MODE) console.log('🔄 Price Breakup Sync: New variant ID:', newVariantId);

          if (newVariantId !== this.currentVariantId) {
            this.currentVariantId = newVariantId;
            if (DEBUG_MODE) console.log('🔄 Price Breakup Sync: Variant ID changed, updating display...');
            this.updateDisplay(newVariantId);
          } else {
            if (DEBUG_MODE) console.log('🔄 Price Breakup Sync: Variant ID unchanged, forcing display update anyway...');
            // Force update even if ID is same (color might have changed)
            this.updateDisplay(newVariantId);
          }
        }
      });

      // Method 2: Monitor the variant selector directly
      // This is the BEST practice - monitor the final variant ID that theme calculates
      const variantSelect = document.querySelector('select[data-variant-select]');
      if (variantSelect) {
        // Helper function to handle variant change with debouncing
        const handleVariantChange = (source) => {
          // Skip if drawer is handling the update
          if (this.optionSelectorsDisabled) {
            if (DEBUG_MODE) console.log(`🔄 Price Breakup Sync: Variant selector changed (${source}), but disabled (drawer handling)`);
            return;
          }

          // Clear any pending update
          if (this.updateTimeout) {
            clearTimeout(this.updateTimeout);
          }

          const immediateVariantId = variantSelect.value;
          if (DEBUG_MODE) console.log(`🔄 Price Breakup Sync: Variant selector changed to: ${immediateVariantId} (${source})`);

          // Debounce: wait for value to stabilize
          this.updateTimeout = setTimeout(() => {
            const finalVariantId = variantSelect.value;
            if (DEBUG_MODE) console.log(`🔄 Price Breakup Sync: After 100ms delay, variant selector shows: ${finalVariantId}`);

            if (finalVariantId && finalVariantId !== this.currentVariantId) {
              this.currentVariantId = finalVariantId;
              if (DEBUG_MODE) console.log('🔄 Price Breakup Sync: Selector change detected, updating display...');
              this.updateDisplay(finalVariantId);
            } else if (finalVariantId === this.currentVariantId) {
              if (DEBUG_MODE) console.log('🔄 Price Breakup Sync: Variant unchanged, skipping update');
            }
          }, 100); // 100ms delay to ensure theme has finished updating
        };

        // Listen to change event (when user selects directly or theme updates it)
        variantSelect.addEventListener('change', () => handleVariantChange('change event'));

        // Also observe value changes via MutationObserver (in case theme updates without firing change event)
        const observer = new MutationObserver(() => handleVariantChange('mutation observer'));

        observer.observe(variantSelect, {
          attributes: true,
          attributeFilter: ['value'],
          childList: true,
          subtree: true
        });

        if (DEBUG_MODE) console.log('Price Breakup Sync: Listening to variant selector changes (change event + mutation observer) with 100ms debounce');
      }

      // Method 2.5: BACKUP - Monitor option selectors and calculate variant ourselves
      const optionSelectors = document.querySelectorAll('select[data-index^="option"]');

      // Get variants from the same source as the drawer
      let productVariants = null;
      const variantDataElement = document.querySelector('[data-drawer-variant-json]');
      if (variantDataElement) {
        try {
          productVariants = JSON.parse(variantDataElement.textContent);
          if (DEBUG_MODE) console.log(`🔍 Price Breakup Sync: Loaded ${productVariants.length} variants from [data-drawer-variant-json]`);
        } catch (e) {
          if (DEBUG_MODE) console.log('❌ Price Breakup Sync: Failed to parse variant JSON:', e);
        }
      } else {
        // Fallback to window.productVariants if available
        productVariants = window.productVariants || null;
        if (productVariants) {
          if (DEBUG_MODE) console.log(`🔍 Price Breakup Sync: Loaded ${productVariants.length} variants from window.productVariants`);
        }
      }

      if (optionSelectors.length > 0 && productVariants) {
        if (DEBUG_MODE) console.log(`🔍 Price Breakup Sync: Found ${optionSelectors.length} option selectors (backup method)`);

        optionSelectors.forEach((optionSelect, index) => {
          optionSelect.addEventListener('change', () => {
            // Skip if drawer is handling the update
            if (this.optionSelectorsDisabled) {
              if (DEBUG_MODE) console.log(`🔄 Option selector ${index + 1} changed, but disabled (drawer handling)`);
              return;
            }

            if (DEBUG_MODE) console.log(`🔄 Price Breakup Sync: Option selector ${index + 1} changed to: "${optionSelect.value}"`);

            // Calculate variant ourselves instead of waiting for theme
            setTimeout(() => {
              // Get current values from all option selectors
              const selectedOptions = {};
              optionSelectors.forEach((select) => {
                const optionIndex = select.getAttribute('data-index').replace('option', '');
                selectedOptions[optionIndex] = select.value;
              });

              if (DEBUG_MODE) console.log('🔍 Price Breakup Sync: Current selected options:', selectedOptions);

              // Find matching variant (same logic as drawer)
              const matchingVariant = productVariants.find(variant => {
                return Object.keys(selectedOptions).every(position => {
                  const optionKey = `option${position}`;
                  return variant[optionKey] === selectedOptions[position];
                });
              });

              if (matchingVariant) {
                const newVariantId = matchingVariant.id.toString();
                if (DEBUG_MODE) console.log(`✅ Price Breakup Sync: Found matching variant: ${newVariantId} (${matchingVariant.title})`);

                if (newVariantId !== this.currentVariantId) {
                  this.currentVariantId = newVariantId;
                  if (DEBUG_MODE) console.log('🔄 Price Breakup Sync: Option selector triggered update');
                  this.updateDisplay(newVariantId);
                } else {
                  if (DEBUG_MODE) console.log('🔄 Price Breakup Sync: Variant unchanged, skipping update');
                }
              } else {
                if (DEBUG_MODE) console.log('❌ Price Breakup Sync: No matching variant found for options:', selectedOptions);
              }
            }, 100); // Small delay to let DOM update
          });
        });

        if (DEBUG_MODE) console.log('Price Breakup Sync: Listening to option selector changes (backup method with variant calculation)');
      }

      // Method 3: Monitor form input changes (for custom drawer and other sources)
      const form = document.querySelector('[data-product-form]');
      if (form) {
        const observer = new MutationObserver(() => {
          const variantInput = form.querySelector('input[name="id"]');
          if (variantInput) {
            const newVariantId = variantInput.value;
            if (newVariantId) {
              if (newVariantId !== this.currentVariantId) {
                if (DEBUG_MODE) console.log('🔄 Price Breakup Sync: Form input changed to:', newVariantId);
                this.currentVariantId = newVariantId;
                if (DEBUG_MODE) console.log('🔄 Price Breakup Sync: Form mutation detected, updating display...');
                this.updateDisplay(newVariantId);
              } else {
                if (DEBUG_MODE) console.log('🔄 Price Breakup Sync: Form input unchanged, forcing update anyway...');
                this.updateDisplay(newVariantId);
              }
            }
          }
        });

        observer.observe(form, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['value']
        });

        if (DEBUG_MODE) console.log('Price Breakup Sync: Monitoring form input mutations');
      }

      if (DEBUG_MODE) console.log('Price Breakup Sync: All variant change listeners set up ✓');
    }

    /**
     * Update all displays with the variant's metafield data
     */
    updateDisplay(variantId) {
      if (DEBUG_MODE) console.log('📊 Price Breakup Sync: Updating display for variant', variantId);

      // DATA SOURCE 1: Gold Price Editor Plugin - Variant Metafields
      // Namespace: DI-GoldPrice, Key: variant_config, Owner: ProductVariant
      const variantData = this.variantMetafields[variantId];

      if (!variantData || !variantData.parsed) {
        if (DEBUG_MODE) console.warn('⚠️ Price Breakup Sync: No metafield data for variant', variantId);
        if (DEBUG_MODE) console.log('Available variants:', Object.keys(this.variantMetafields));
        return;
      }

      const config = variantData.parsed;

      // DATA SOURCE 2: Gold Price Editor Plugin - Metal Prices
      // Namespace: DI-GoldPrice, Key: metal_prices, Owner: Shop
      const shopData = this.shopMetafields.parsed;

      if (DEBUG_MODE) console.log('📦 Gold Price Editor - Variant Metafields (DI-GoldPrice.variant_config):', config);
      if (DEBUG_MODE) console.log('💰 Gold Price Editor - Metal Prices (DI-GoldPrice.metal_prices):', shopData);

      // Small delay to let the theme update the DOM with selected values
      // The customize button works because it uses the same [data-selected-value-for-option] elements
      // We need to wait for those to be updated
      setTimeout(() => {
        if (DEBUG_MODE) console.log('⏰ Price Breakup Sync: 100ms delay elapsed, reading color from DOM...');

        // DATA SOURCE 3: Shopify Product Variants (Color, Size, etc.)
        // Source: Product variant options (NOT metafields), accessed via DOM
        const metalColor = this.getMetalColorFromVariant();

        if (DEBUG_MODE) console.log('🎨 Shopify Variants - Metal Color (from DOM):', metalColor);

        // Calculate prices
        const calc = this.calculatePrices(config, shopData, metalColor);
        if (DEBUG_MODE) console.log('🧮 Calculated values:', calc);
        if (DEBUG_MODE) console.log('🔍 Purity from metafield:', config.purity, '→ Display will show:', calc.purity + 'T ' + calc.metalColor);

        // Update both tabs
        this.updateProductDetailsTab(calc);
        this.updatePriceBreakupTab(calc, variantId, config, shopData);

        if (DEBUG_MODE) console.log('✅ Updated Product Details Tab - Purity label to:', calc.purity + 'T ' + calc.metalColor);
        if (DEBUG_MODE) console.log('✅ Updated Price Breakup Tab - Purity label to:', calc.purity + 'T ' + calc.metalColor);

        if (DEBUG_MODE) console.log('✅ Price Breakup Sync: Display updated ✓');
      }, 100); // 100ms delay to let theme update the DOM
    }

    /**
     * Get metal color from the selected variant options
     * Color is typically one of the product options (e.g., "Colour" option with values like "Yellow Gold", "White Gold")
     */
    getMetalColorFromVariant() {
      if (DEBUG_MODE) console.log('🔍 Searching for metal color from variant options...');

      // Look for the color option in the variant picker
      // Common option names: "Colour", "Color", "Metal", "Metal Color"
      const colorOptionNames = ['colour', 'color', 'metal', 'metal color', 'metal colour'];

      // Find the color option from the selected values
      const selectedValueElements = document.querySelectorAll('[data-selected-value-for-option]');
      if (DEBUG_MODE) console.log(`🔍 Found ${selectedValueElements.length} [data-selected-value-for-option] elements`);

      for (const valueElement of selectedValueElements) {
        const optionName = valueElement.getAttribute('data-option-name');
        if (DEBUG_MODE) console.log(`  Checking element with data-option-name="${optionName}"`);

        if (!optionName) continue;

        // Get the option label to check if it's a color option
        const optionWrapper = valueElement.closest('[data-product-option]');
        if (!optionWrapper) {
          if (DEBUG_MODE) console.log(`  No [data-product-option] wrapper found`);
          continue;
        }

        const labelElement = optionWrapper.querySelector('.product__label');
        if (!labelElement) {
          if (DEBUG_MODE) console.log(`  No .product__label found`);
          continue;
        }

        // Try multiple methods to get the label text
        let labelText = '';

        // Method 1: Get first text node (before any child elements/newlines)
        if (labelElement.firstChild && labelElement.firstChild.nodeType === 3) {
          labelText = labelElement.firstChild.textContent.trim().toLowerCase().replace(':', '');
          if (DEBUG_MODE) console.log(`  Method 1 - First text node: "${labelText}"`);
        }

        // Method 2: Split on newline and take first non-empty part
        if (!labelText) {
          const parts = labelElement.textContent.split('\n').map(p => p.trim()).filter(p => p);
          if (parts.length > 0) {
            labelText = parts[0].toLowerCase().replace(':', '');
            if (DEBUG_MODE) console.log(`  Method 2 - Split newline: "${labelText}"`);
          }
        }

        // Method 3: Use the data-option-name attribute directly
        if (!labelText && optionName) {
          // Convert option1 -> purity mapping if needed
          const optionNumber = optionName.replace('option', '');
          const optionIndex = parseInt(optionNumber) - 1;
          // You could add a mapping here, but for now just use what we have
          if (DEBUG_MODE) console.log(`  Method 3 - Using optionName: "${optionName}"`);
        }

        if (DEBUG_MODE) console.log(`  Label text (final): "${labelText}"`);

        // Check if this is the color option
        if (labelText && colorOptionNames.includes(labelText)) {
          // Get color value and clean it up (capitalize properly)
          const colorValue = valueElement.textContent.trim();
          // Capitalize each word: "yellow gold" → "Yellow Gold"
          const capitalizedColor = colorValue
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');

          if (DEBUG_MODE) console.log(`✅ Found metal color from "${labelText}" option:`, capitalizedColor);
          return capitalizedColor;
        }
      }

      if (DEBUG_MODE) console.log('🔍 Color not found in [data-selected-value-for-option], trying variant selector...');

      // Fallback: try to get from the selected option text in variant selector
      const variantSelect = document.querySelector('select[data-variant-select]');
      if (variantSelect) {
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        if (selectedOption) {
          const optionText = selectedOption.textContent.trim();
          if (DEBUG_MODE) console.log(`🔍 Variant selector text: "${optionText}"`);
          // Try to extract color from variant title (e.g., "14K / Yellow Gold / 12.5")
          const colorMatch = optionText.match(/(Yellow Gold|White Gold|Rose Gold|Pink Gold)/i);
          if (colorMatch) {
            if (DEBUG_MODE) console.log('✅ Found metal color from variant title:', colorMatch[1]);
            return colorMatch[1];
          }
        }
      } else {
        if (DEBUG_MODE) console.log('⚠️ No variant selector found');
      }

      if (DEBUG_MODE) console.warn('⚠️ Could not determine metal color from variant options, using default');
      return 'Yellow Gold'; // Default fallback
    }

    /**
     * Calculate all pricing values
     * Simple approach: Gold only with wastage charges
     */
    calculatePrices(config, shopData, metalColor) {
      const metalWeight = config.metal_weight || 0;
      const purity = config.purity || '14K';
      const wastageCharges = config.wastage_charges || 0;

      // Get gold price per gram based on purity
      // Normalize so formatting drift in the metafield (e.g. "9KT", "9 K", lowercase) still matches
      const normalizedPurity = purity.toString().trim().toUpperCase().replace(/\s+/g, '').replace(/KT$/, 'K');
      let goldRate = 0;
      switch (normalizedPurity) {
        case '24K': goldRate = shopData.gold_price_24k || 0; break;
        case '22K': goldRate = shopData.gold_price_22k || 0; break;
        case '21K': goldRate = shopData.gold_price_21k || 0; break;
        case '18K': goldRate = shopData.gold_price_18k || 0; break;
        case '14K': goldRate = shopData.gold_price_14k || 0; break;
        case '10K': goldRate = shopData.gold_price_10k || 0; break;
        case '9K': goldRate = shopData.gold_price_9k || 0; break;
        case '8K': goldRate = shopData.gold_price_8k || 0; break;
        default:
          if (DEBUG_MODE) console.warn(`⚠️ Unrecognized gold purity "${purity}", falling back to 14K rate`);
          goldRate = shopData.gold_price_14k || 0;
      }

      // Calculate gold total (base metal cost only, wastage moved to making)
      const goldTotal = metalWeight * goldRate;

      const makingCharges = config.making_charges || 0;
      const makingWithWastage = makingCharges + wastageCharges; // Making charges includes wastage
      const diamondCharges = config.diamond_charges || 0;
      const polkiCharges = config.insurance_charges || 0; // Polki charges (mapped from insurance_charges)
      const stoneCharges = config.stone_charges || 0; // Gemstone charges
      const moissaniteCharges = config.moissanite_charges || 0; // Moissanite charges
      const miscCharges = config.misc_charges || 0;

      const subtotal = goldTotal + makingWithWastage + diamondCharges + polkiCharges + stoneCharges + moissaniteCharges + miscCharges;
      const taxRate = config.tax || 3;
      const taxAmount = (subtotal * taxRate) / 100;
      const totalPrice = subtotal + taxAmount;

      // Get metal type from config for display
      const metalType = config.metal_type || 'gold';

      return {
        purity,
        metalType,
        metalColor,
        metalWeight,
        goldRate,
        goldTotal,
        wastageCharges,
        makingCharges,
        makingWithWastage, // Total making (making + wastage)
        diamondCharges,
        polkiCharges, // Polki charges
        stoneCharges, // Gemstone charges
        moissaniteCharges, // Moissanite charges
        miscCharges,
        subtotal,
        taxRate,
        taxAmount,
        totalPrice
      };
    }

    /**
     * Update Product Details Tab
     */
    updateProductDetailsTab(calc) {
      // Gold purity label - Format: "Purity Metal (Colour)" or "Purity Metal"
      const goldPurityLabel = document.querySelector('[data-gold-purity-label]');
      if (goldPurityLabel) {
        const metalName = calc.metalType.charAt(0).toUpperCase() + calc.metalType.slice(1);
        let newText = `${calc.purity}T ${metalName}`;

        // Only add color in brackets if it exists and is not empty
        // Color is styled with smaller, lighter font
        if (calc.metalColor && calc.metalColor.trim() !== '') {
          newText += ` <span style="font-size: 0.85em; font-weight: normal;">(${calc.metalColor})</span>`;
        }

        if (DEBUG_MODE) console.log('📝 Product Details - Updating [data-gold-purity-label]:', newText);
        goldPurityLabel.innerHTML = newText;
      } else {
        if (DEBUG_MODE) console.warn('⚠️ Product Details - [data-gold-purity-label] element not found');
      }

      // Gold rate
      const goldRate = document.querySelector('[data-gold-rate]');
      if (goldRate) {
        goldRate.textContent = this.formatMoney(calc.goldRate);
      }

      // Gold weight
      const goldWeight = document.querySelector('[data-gold-weight]');
      if (goldWeight) {
        goldWeight.textContent = `${calc.metalWeight.toFixed(3)} gms`;
      }

      // Making rate per gram
      const makingRate = document.querySelector('[data-making-rate]');
      if (makingRate) {
        const makingRatePerGram = calc.metalWeight > 0 ? calc.makingCharges / calc.metalWeight : 0;
        makingRate.textContent = this.formatMoney(makingRatePerGram);
      }

      // Making total
      const makingTotal = document.querySelector('[data-making-total]');
      if (makingTotal) {
        makingTotal.textContent = this.formatMoney(calc.makingCharges);
      }
    }

    /**
     * Update Price Breakup Tab
     */
    updatePriceBreakupTab(calc, variantId, config, shopData) {
      // GOLD Section Title
      const priceGoldSectionTitle = document.querySelector('[data-price-gold-section-title]');
      if (priceGoldSectionTitle) {
        const metalName = calc.metalType.charAt(0).toUpperCase() + calc.metalType.slice(1);
        priceGoldSectionTitle.textContent = metalName.toUpperCase();
      }

      // GOLD Section
      const priceGoldPurityLabel = document.querySelector('[data-price-gold-purity-label]');
      if (priceGoldPurityLabel) {
        // Format: "Purity Metal (Colour)" or "Purity Metal" if no colour
        // Example: "14KT Gold (Rose Gold)" or "14KT Gold"
        const metalName = calc.metalType.charAt(0).toUpperCase() + calc.metalType.slice(1);
        let newText = `${calc.purity}T ${metalName}`;

        // Only add color in brackets if it exists and is not empty
        // Color is styled with smaller, lighter font
        if (calc.metalColor && calc.metalColor.trim() !== '') {
          newText += ` <span style="font-size: 0.85em; font-weight: normal;">(${calc.metalColor})</span>`;
        }

        if (DEBUG_MODE) console.log('📝 Price Breakup - Updating [data-price-gold-purity-label]:', newText);
        priceGoldPurityLabel.innerHTML = newText;
      } else {
        if (DEBUG_MODE) console.warn('⚠️ Price Breakup - [data-price-gold-purity-label] element not found');
      }

      const priceGoldRate = document.querySelector('[data-price-gold-rate]');
      if (priceGoldRate) {
        priceGoldRate.textContent = this.formatMoney(calc.goldRate);
      }

      const priceGoldWeight = document.querySelector('[data-price-gold-weight]');
      if (priceGoldWeight) {
        priceGoldWeight.textContent = `${calc.metalWeight.toFixed(3)} gms`;
      }

      const priceGoldTotal = document.querySelector('[data-price-gold-total]');
      if (priceGoldTotal) {
        priceGoldTotal.textContent = this.formatMoney(calc.goldTotal);
      }

      const priceGoldSummary = document.querySelector('[data-price-gold-summary]');
      if (priceGoldSummary) {
        priceGoldSummary.textContent = this.formatMoney(calc.goldTotal);
      }

      // MAKING CHARGES Section
      const priceMakingPurityLabel = document.querySelector('[data-price-making-purity-label]');
      if (priceMakingPurityLabel) {
        const metalName = calc.metalType.charAt(0).toUpperCase() + calc.metalType.slice(1);
        let newText = `${calc.purity}T ${metalName}`;

        // Only add color in brackets if it exists and is not empty
        // Color is styled with smaller, lighter font
        if (calc.metalColor && calc.metalColor.trim() !== '') {
          newText += ` <span style="font-size: 0.85em; font-weight: normal;">(${calc.metalColor})</span>`;
        }

        if (DEBUG_MODE) console.log('📝 Price Breakup - Updating [data-price-making-purity-label]:', newText);
        priceMakingPurityLabel.innerHTML = newText;
      } else {
        if (DEBUG_MODE) console.warn('⚠️ Price Breakup - [data-price-making-purity-label] element not found');
      }

      const priceMakingRateDisplay = document.querySelector('[data-price-making-rate-display]');
      if (priceMakingRateDisplay) {
        const makingRatePerGram = calc.metalWeight > 0 ? calc.makingCharges / calc.metalWeight : 0;
        priceMakingRateDisplay.textContent = this.formatMoney(makingRatePerGram);
      }

      const priceMakingWeight = document.querySelector('[data-price-making-weight]');
      if (priceMakingWeight) {
        priceMakingWeight.textContent = `${calc.metalWeight.toFixed(3)} gms`;
      }

      // Wastage charges - only show if value > 0 (now in MAKING section)
      const priceWastageRow = document.querySelector('[data-price-wastage-row]');
      const priceWastageCharges = document.querySelector('[data-price-wastage-charges]');

      if (calc.wastageCharges > 0) {
        // Show row and update value
        if (priceWastageRow) priceWastageRow.style.display = '';
        if (priceWastageCharges) priceWastageCharges.textContent = this.formatMoney(calc.wastageCharges);
      } else {
        // Hide row if wastage is 0
        if (priceWastageRow) priceWastageRow.style.display = 'none';
      }

      const priceMakingTotal = document.querySelector('[data-price-making-total]');
      if (priceMakingTotal) {
        priceMakingTotal.textContent = this.formatMoney(calc.makingWithWastage); // Total with wastage
      }

      const priceMakingSummary = document.querySelector('[data-price-making-summary]');
      if (priceMakingSummary) {
        priceMakingSummary.textContent = this.formatMoney(calc.makingWithWastage); // Total with wastage
      }

      // DIAMONDS Section
      // Update diamond cards from metafield
      this.updateDiamondCards(variantId, config);

      const priceDiamondsSummary = document.querySelector('[data-price-diamonds-summary]');
      if (priceDiamondsSummary) {
        priceDiamondsSummary.textContent = this.formatMoney(calc.diamondCharges);
      }

      // POLKI Section
      // Update polki cards from metafield
      this.updatePolkiCards(variantId, config);

      const pricePolkiSummary = document.querySelector('[data-price-polki-summary]');
      if (pricePolkiSummary) {
        pricePolkiSummary.textContent = this.formatMoney(calc.polkiCharges);
      }

      // MOISSANITE Section
      // Update moissanite cards from metafield
      this.updateMoissaniteCards(variantId, config);

      const priceMoissaniteSummary = document.querySelector('[data-price-moissanite-summary]');
      if (priceMoissaniteSummary) {
        priceMoissaniteSummary.textContent = this.formatMoney(calc.moissaniteCharges);
      }

      // GEMSTONES Section
      // Update gemstone cards from metafield
      this.updateGemstoneCards(variantId, config);

      const priceGemstonesSummary = document.querySelector('[data-price-gemstones-summary]');
      if (priceGemstonesSummary) {
        priceGemstonesSummary.textContent = this.formatMoney(calc.stoneCharges);
      }

      // FINAL Section
      const finalGoldTotal = document.querySelector('[data-final-gold-total]');
      if (finalGoldTotal) {
        finalGoldTotal.textContent = this.formatMoney(calc.goldTotal);
      }

      const finalMakingTotal = document.querySelector('[data-final-making-total]');
      if (finalMakingTotal) {
        finalMakingTotal.textContent = this.formatMoney(calc.makingCharges);
      }

      const finalDiamondsTotal = document.querySelector('[data-final-diamonds-total]');
      if (finalDiamondsTotal) {
        finalDiamondsTotal.textContent = this.formatMoney(calc.diamondCharges);
      }

      const finalPolkiTotal = document.querySelector('[data-final-polki-total]');
      if (finalPolkiTotal) {
        finalPolkiTotal.textContent = this.formatMoney(calc.polkiCharges);
      }

      const finalGemstonesTotal = document.querySelector('[data-final-gemstones-total]');
      if (finalGemstonesTotal) {
        finalGemstonesTotal.textContent = this.formatMoney(calc.stoneCharges);
      }

      const finalMoissaniteTotal = document.querySelector('[data-final-moissanite-total]');
      if (finalMoissaniteTotal) {
        finalMoissaniteTotal.textContent = this.formatMoney(calc.moissaniteCharges);
      }

      const finalMiscCharges = document.querySelector('[data-final-misc-charges]');
      if (finalMiscCharges) {
        finalMiscCharges.textContent = this.formatMoney(calc.miscCharges);
      }

      const finalTaxRate = document.querySelector('[data-final-tax-rate]');
      if (finalTaxRate) {
        finalTaxRate.textContent = `${calc.taxRate}%`;
      }

      const finalTaxAmount = document.querySelector('[data-final-tax-amount]');
      if (finalTaxAmount) {
        finalTaxAmount.textContent = this.formatMoney(calc.taxAmount);
      }

      const finalTotalPrice = document.querySelector('[data-final-total-price]');
      if (finalTotalPrice) {
        finalTotalPrice.textContent = this.formatMoney(calc.totalPrice);
      }

      // ========================================
      // SECTION VISIBILITY LOGIC
      // ========================================
      if (DEBUG_MODE) console.log('👁️ Updating section visibility based on values...');

      // GOLD/MAKING Section - Hide only if BOTH are zero
      const goldMakingSection = document.querySelector('[data-gold-making-section]');
      if (goldMakingSection) {
        const shouldHide = calc.goldTotal === 0 && calc.makingWithWastage === 0;
        goldMakingSection.style.display = shouldHide ? 'none' : '';
        if (DEBUG_MODE) console.log(`  GOLD/MAKING Section: ${shouldHide ? 'HIDDEN' : 'VISIBLE'} (goldTotal: ${calc.goldTotal}, makingWithWastage: ${calc.makingWithWastage})`);
      }

      // DIAMONDS Section - Hide if zero
      const diamondsSection = document.querySelector('[data-diamonds-section]');
      if (diamondsSection) {
        const shouldHide = calc.diamondCharges === 0;
        diamondsSection.style.display = shouldHide ? 'none' : '';
        if (DEBUG_MODE) console.log(`  DIAMONDS Section: ${shouldHide ? 'HIDDEN' : 'VISIBLE'} (diamondCharges: ${calc.diamondCharges})`);
      }

      // POLKI Section - Hide if zero
      const polkiSection = document.querySelector('[data-polki-section]');
      if (polkiSection) {
        const shouldHide = calc.polkiCharges === 0;
        polkiSection.style.display = shouldHide ? 'none' : '';
        if (DEBUG_MODE) console.log(`  POLKI Section: ${shouldHide ? 'HIDDEN' : 'VISIBLE'} (polkiCharges: ${calc.polkiCharges})`);
      }

      // MOISSANITE Section - Hide if zero
      const moissaniteSection = document.querySelector('[data-moissanite-section]');
      if (moissaniteSection) {
        const shouldHide = calc.moissaniteCharges === 0;
        moissaniteSection.style.display = shouldHide ? 'none' : '';
        if (DEBUG_MODE) console.log(`  MOISSANITE Section: ${shouldHide ? 'HIDDEN' : 'VISIBLE'} (moissaniteCharges: ${calc.moissaniteCharges})`);
      }

      // GEMSTONES Section - Hide if zero
      const gemstonesSection = document.querySelector('[data-gemstones-section]');
      if (gemstonesSection) {
        const shouldHide = calc.stoneCharges === 0;
        gemstonesSection.style.display = shouldHide ? 'none' : '';
        if (DEBUG_MODE) console.log(`  GEMSTONES Section: ${shouldHide ? 'HIDDEN' : 'VISIBLE'} (stoneCharges: ${calc.stoneCharges})`);
      }

      // FINAL Section Items - Hide individual items if zero (NOT tax/total)
      const finalGoldItem = document.querySelector('[data-final-gold-item]');
      if (finalGoldItem) {
        const shouldHide = calc.goldTotal === 0;
        finalGoldItem.style.display = shouldHide ? 'none' : '';
        if (DEBUG_MODE) console.log(`  FINAL Gold Item: ${shouldHide ? 'HIDDEN' : 'VISIBLE'} (goldTotal: ${calc.goldTotal})`);
      }

      const finalMakingItem = document.querySelector('[data-final-making-item]');
      if (finalMakingItem) {
        const shouldHide = calc.makingWithWastage === 0;
        finalMakingItem.style.display = shouldHide ? 'none' : '';
        if (DEBUG_MODE) console.log(`  FINAL Making Item: ${shouldHide ? 'HIDDEN' : 'VISIBLE'} (makingWithWastage: ${calc.makingWithWastage})`);
      }

      const finalDiamondsItem = document.querySelector('[data-final-diamonds-item]');
      if (finalDiamondsItem) {
        const shouldHide = calc.diamondCharges === 0;
        finalDiamondsItem.style.display = shouldHide ? 'none' : '';
        if (DEBUG_MODE) console.log(`  FINAL Diamonds Item: ${shouldHide ? 'HIDDEN' : 'VISIBLE'} (diamondCharges: ${calc.diamondCharges})`);
      }

      const finalPolkiItem = document.querySelector('[data-final-polki-item]');
      if (finalPolkiItem) {
        const shouldHide = calc.polkiCharges === 0;
        finalPolkiItem.style.display = shouldHide ? 'none' : '';
        if (DEBUG_MODE) console.log(`  FINAL Polki Item: ${shouldHide ? 'HIDDEN' : 'VISIBLE'} (polkiCharges: ${calc.polkiCharges})`);
      }

      const finalMoissaniteItem = document.querySelector('[data-final-moissanite-item]');
      if (finalMoissaniteItem) {
        const shouldHide = calc.moissaniteCharges === 0;
        finalMoissaniteItem.style.display = shouldHide ? 'none' : '';
        if (DEBUG_MODE) console.log(`  FINAL Moissanite Item: ${shouldHide ? 'HIDDEN' : 'VISIBLE'} (moissaniteCharges: ${calc.moissaniteCharges})`);
      }

      const finalGemstonesItem = document.querySelector('[data-final-gemstones-item]');
      if (finalGemstonesItem) {
        const shouldHide = calc.stoneCharges === 0;
        finalGemstonesItem.style.display = shouldHide ? 'none' : '';
        if (DEBUG_MODE) console.log(`  FINAL Gemstones Item: ${shouldHide ? 'HIDDEN' : 'VISIBLE'} (stoneCharges: ${calc.stoneCharges})`);
      }

      const finalMiscItem = document.querySelector('[data-final-misc-item]');
      if (finalMiscItem) {
        const shouldHide = calc.miscCharges === 0;
        finalMiscItem.style.display = shouldHide ? 'none' : '';
        if (DEBUG_MODE) console.log(`  FINAL Misc Item: ${shouldHide ? 'HIDDEN' : 'VISIBLE'} (miscCharges: ${calc.miscCharges})`);
      }

      // Tax and Total are ALWAYS visible (even if zero)
      if (DEBUG_MODE) console.log('  Tax and Total: ALWAYS VISIBLE');

      // DEBUG Section - Populate all metafield values
      this.updateDebugSection(variantId, config, shopData, calc, calc.metalColor);
    }

    /**
     * Update diamond cards from variant metafield
     * Dynamically renders cards based on diamond_details JSON metafield
     */
    updateDiamondCards(variantId, config) {
      if (DEBUG_MODE) console.log('💎 Updating diamond cards for variant:', variantId);

      // Get the diamond cards container (Container 1)
      const cardsContainer = document.querySelector('[data-diamonds-cards-container]');
      if (!cardsContainer) {
        if (DEBUG_MODE) console.warn('⚠️ Diamond cards container [data-diamonds-cards-container] not found');
        return;
      }

      // Get diamond_details from variant metafield
      // diamond_details is stored directly on variantData (loaded from custom.diamond_details)
      const variantData = this.variantMetafields[variantId];
      const diamondDetails = variantData && variantData.diamond_details
        ? variantData.diamond_details
        : null;

      if (DEBUG_MODE) console.log('💎 Diamond details from metafield:', diamondDetails);

      // Clear existing cards
      cardsContainer.innerHTML = '';

      // If diamond_details exists and is an array, render cards
      if (diamondDetails && Array.isArray(diamondDetails) && diamondDetails.length > 0) {
        if (DEBUG_MODE) console.log(`💎 Rendering ${diamondDetails.length} diamond cards`);

        // Sort by size in descending order (highest carat first)
        const sortedDiamonds = [...diamondDetails].sort((a, b) => b.size - a.size);

        sortedDiamonds.forEach((diamond, index) => {
          const card = document.createElement('div');
          card.className = 'price-card';

          card.innerHTML = `
            <h4 class="price-item-heading">${diamond.size} Carat</h4>
            <p class="price-detail">Number of Diamonds: <span class="price-value">${diamond.number_of_diamonds}</span></p>
            <p class="price-detail">Total Carat: <span class="price-value">${diamond.total_carat} ct</span></p>
            <p class="price-detail">Colour + Clarity: <span class="price-value">${diamond.color_clarity}</span></p>
            <p class="price-detail price-total">Total: <span class="price-value">Rs. ${diamond.price}</span></p>
          `;

          cardsContainer.appendChild(card);

          if (DEBUG_MODE) console.log(`  ✓ Rendered diamond card ${index + 1}: ${diamond.size} Carat`);
        });
      } else {
        if (DEBUG_MODE) console.log('💎 No diamond details found, no cards rendered');
      }
    }

    /**
     * Update moissanite cards from variant metafield
     * Dynamically renders cards based on moissanite_details JSON metafield
     */
    updateMoissaniteCards(variantId, config) {
      if (DEBUG_MODE) console.log('💎 Updating moissanite cards for variant:', variantId);

      // Get the moissanite cards container (Container 1)
      const cardsContainer = document.querySelector('[data-moissanite-cards-container]');
      if (!cardsContainer) {
        if (DEBUG_MODE) console.warn('⚠️ Moissanite cards container [data-moissanite-cards-container] not found');
        return;
      }

      // Get moissanite_details from variant metafield
      // moissanite_details is stored directly on variantData (loaded from custom.moissanite_details)
      const variantData = this.variantMetafields[variantId];
      const moissaniteDetails = variantData && variantData.moissanite_details
        ? variantData.moissanite_details
        : null;

      if (DEBUG_MODE) console.log('💎 Moissanite details from metafield:', moissaniteDetails);

      // Clear existing cards
      cardsContainer.innerHTML = '';

      // If moissanite_details exists and is an array, render cards
      if (moissaniteDetails && Array.isArray(moissaniteDetails) && moissaniteDetails.length > 0) {
        if (DEBUG_MODE) console.log(`💎 Rendering ${moissaniteDetails.length} moissanite cards`);

        // Sort by size in descending order (highest carat first)
        const sortedMoissanites = [...moissaniteDetails].sort((a, b) => b.size - a.size);

        sortedMoissanites.forEach((moissanite, index) => {
          const card = document.createElement('div');
          card.className = 'price-card';

          card.innerHTML = `
            <h4 class="price-item-heading">${moissanite.size} Carat</h4>
            <p class="price-detail">Number of Moissanites: <span class="price-value">${moissanite.number_of_moissanites}</span></p>
            <p class="price-detail">Total Carat: <span class="price-value">${moissanite.total_carat} ct</span></p>
            <p class="price-detail">Colour + Clarity: <span class="price-value">${moissanite.color_clarity}</span></p>
            <p class="price-detail price-total">Total: <span class="price-value">Rs. ${moissanite.price}</span></p>
          `;

          cardsContainer.appendChild(card);

          if (DEBUG_MODE) console.log(`  ✓ Rendered moissanite card ${index + 1}: ${moissanite.size} Carat`);
        });
      } else {
        if (DEBUG_MODE) console.log('💎 No moissanite details found, no cards rendered');
      }
    }

    /**
     * Update polki cards from variant metafield
     * Dynamically renders cards based on polki_details JSON metafield
     */
    updatePolkiCards(variantId, config) {
      if (DEBUG_MODE) console.log('💎 Updating polki cards for variant:', variantId);

      // Get the polki cards container (Container 1)
      const cardsContainer = document.querySelector('[data-polki-cards-container]');
      if (!cardsContainer) {
        if (DEBUG_MODE) console.warn('⚠️ Polki cards container [data-polki-cards-container] not found');
        return;
      }

      // Get polki_details from variant metafield
      // polki_details is stored directly on variantData (loaded from custom.polki_details)
      const variantData = this.variantMetafields[variantId];
      const polkiDetails = variantData && variantData.polki_details
        ? variantData.polki_details
        : null;

      if (DEBUG_MODE) console.log('💎 Polki details from metafield:', polkiDetails);

      // Clear existing cards
      cardsContainer.innerHTML = '';

      // If polki_details exists and is an array, render cards
      if (polkiDetails && Array.isArray(polkiDetails) && polkiDetails.length > 0) {
        if (DEBUG_MODE) console.log(`💎 Rendering ${polkiDetails.length} polki cards`);

        // Sort by size in descending order (highest carat first)
        const sortedPolki = [...polkiDetails].sort((a, b) => b.size - a.size);

        sortedPolki.forEach((polki, index) => {
          const card = document.createElement('div');
          card.className = 'price-card';

          card.innerHTML = `
            <h4 class="price-item-heading">${polki.size} Carat</h4>
            <p class="price-detail">Number of Polki: <span class="price-value">${polki.number_of_polki}</span></p>
            <p class="price-detail">Total Carat: <span class="price-value">${polki.total_carat} ct</span></p>
            <p class="price-detail">Colour + Clarity: <span class="price-value">${polki.color_clarity}</span></p>
            <p class="price-detail price-total">Total: <span class="price-value">Rs. ${polki.price}</span></p>
          `;

          cardsContainer.appendChild(card);

          if (DEBUG_MODE) console.log(`  ✓ Rendered polki card ${index + 1}: ${polki.size} Carat`);
        });
      } else {
        if (DEBUG_MODE) console.log('💎 No polki details found, no cards rendered');
      }
    }

    /**
     * Update gemstone cards from variant metafield
     * Dynamically renders cards based on gemstone_details JSON metafield
     */
    updateGemstoneCards(variantId, config) {
      if (DEBUG_MODE) console.log('💎 Updating gemstone cards for variant:', variantId);

      // Get the gemstone cards container (Container 1)
      const cardsContainer = document.querySelector('[data-gemstones-cards-container]');
      if (!cardsContainer) {
        if (DEBUG_MODE) console.warn('⚠️ Gemstone cards container [data-gemstones-cards-container] not found');
        return;
      }

      // Get gemstone_details from variant metafield
      // gemstone_details is stored directly on variantData (loaded from custom.gemstone_details)
      const variantData = this.variantMetafields[variantId];
      const gemstoneDetails = variantData && variantData.gemstone_details
        ? variantData.gemstone_details
        : null;

      if (DEBUG_MODE) console.log('💎 Gemstone details from metafield:', gemstoneDetails);

      // Clear existing cards
      cardsContainer.innerHTML = '';

      // If gemstone_details exists and is an array, render cards
      if (gemstoneDetails && Array.isArray(gemstoneDetails) && gemstoneDetails.length > 0) {
        if (DEBUG_MODE) console.log(`💎 Rendering ${gemstoneDetails.length} gemstone cards`);

        // Sort by total_carat in descending order (highest carat first)
        const sortedGemstones = [...gemstoneDetails].sort((a, b) => b.total_carat - a.total_carat);

        sortedGemstones.forEach((gemstone, index) => {
          const card = document.createElement('div');
          card.className = 'price-card';

          card.innerHTML = `
            <h4 class="price-item-heading">${gemstone.type}</h4>
            <p class="price-detail">Number of Stones: <span class="price-value">${gemstone.number_of_stones}</span></p>
            <p class="price-detail">Total Carat: <span class="price-value">${gemstone.total_carat} ct</span></p>
            <p class="price-detail">Quality: <span class="price-value">${gemstone.quality}</span></p>
            <p class="price-detail price-total">Total: <span class="price-value">Rs. ${gemstone.price}</span></p>
          `;

          cardsContainer.appendChild(card);

          if (DEBUG_MODE) console.log(`  ✓ Rendered gemstone card ${index + 1}: ${gemstone.type}`);
        });
      } else {
        if (DEBUG_MODE) console.log('💎 No gemstone details found, no cards rendered');
      }
    }

    /**
     * Update debug section with all JSON values
     */
    updateDebugSection(variantId, config, shopData, calc, metalColor) {
      // Hide or show DEBUG section based on DEBUG_MODE flag
      const debugSection = document.querySelector('.price-breakdown-debug');
      if (debugSection) {
        debugSection.style.display = DEBUG_MODE ? '' : 'none';
        if (DEBUG_MODE) console.log('🐛 DEBUG SECTION - Visibility set to:', DEBUG_MODE ? 'VISIBLE' : 'HIDDEN');
      }

      // Only populate debug section if DEBUG_MODE is enabled
      if (!DEBUG_MODE) {
        return;
      }

      if (DEBUG_MODE) console.log('🐛 DEBUG SECTION - Starting update');
      if (DEBUG_MODE) console.log('  variantId:', variantId);
      if (DEBUG_MODE) console.log('  config:', config);
      if (DEBUG_MODE) console.log('  shopData:', shopData);
      if (DEBUG_MODE) console.log('  calc:', calc);
      if (DEBUG_MODE) console.log('  metalColor:', metalColor);

      // Test if elements exist
      const testElement = document.querySelector('[data-debug-purity]');
      if (DEBUG_MODE) console.log('  Test querySelector for [data-debug-purity]:', testElement);

      // Variant Metafields
      const debugPurity = document.querySelector('[data-debug-purity]');
      if (debugPurity) {
        debugPurity.textContent = config.purity || 'N/A';
        if (DEBUG_MODE) console.log('  ✓ Set debugPurity to:', debugPurity.textContent);
      } else {
        if (DEBUG_MODE) console.log('  ✗ debugPurity element not found');
      }

      const debugMetalType = document.querySelector('[data-debug-metal-type]');
      if (debugMetalType) {
        debugMetalType.textContent = config.metal_type || 'N/A';
        if (DEBUG_MODE) console.log('  ✓ Set debugMetalType to:', debugMetalType.textContent);
      } else {
        if (DEBUG_MODE) console.log('  ✗ debugMetalType element not found');
      }

      const debugMetalWeight = document.querySelector('[data-debug-metal-weight]');
      if (debugMetalWeight) debugMetalWeight.textContent = config.metal_weight ? `${config.metal_weight} gm` : 'N/A';

      const debugDiamondCharges = document.querySelector('[data-debug-diamond-charges]');
      if (debugDiamondCharges) debugDiamondCharges.textContent = this.formatMoney(config.diamond_charges || 0);

      const debugPolkiCharges = document.querySelector('[data-debug-polki-charges]');
      if (debugPolkiCharges) debugPolkiCharges.textContent = this.formatMoney(config.insurance_charges || 0);

      const debugMoissaniteCharges = document.querySelector('[data-debug-moissanite-charges]');
      if (debugMoissaniteCharges) debugMoissaniteCharges.textContent = this.formatMoney(config.moissanite_charges || 0);

      const debugStoneCharges = document.querySelector('[data-debug-stone-charges]');
      if (debugStoneCharges) debugStoneCharges.textContent = this.formatMoney(config.stone_charges || 0);

      const debugMakingCharges = document.querySelector('[data-debug-making-charges]');
      if (debugMakingCharges) debugMakingCharges.textContent = this.formatMoney(config.making_charges || 0);

      const debugWastageCharges = document.querySelector('[data-debug-wastage-charges]');
      if (debugWastageCharges) debugWastageCharges.textContent = this.formatMoney(config.wastage_charges || 0);

      const debugInsuranceCharges = document.querySelector('[data-debug-insurance-charges]');
      if (debugInsuranceCharges) debugInsuranceCharges.textContent = this.formatMoney(config.insurance_charges || 0);

      const debugMiscCharges = document.querySelector('[data-debug-misc-charges]');
      if (debugMiscCharges) debugMiscCharges.textContent = this.formatMoney(config.misc_charges || 0);

      const debugMarkupPercentage = document.querySelector('[data-debug-markup-percentage]');
      if (debugMarkupPercentage) debugMarkupPercentage.textContent = config.markup_percentage ? `${config.markup_percentage}%` : 'N/A';

      const debugComparePriceMargin = document.querySelector('[data-debug-compare-price-margin]');
      if (debugComparePriceMargin) debugComparePriceMargin.textContent = config.compare_price_margin ? `${config.compare_price_margin}%` : 'N/A';

      const debugTax = document.querySelector('[data-debug-tax]');
      if (debugTax) debugTax.textContent = `${config.tax || 0}%`;

      const debugBottomPrice = document.querySelector('[data-debug-bottom-price]');
      if (debugBottomPrice) debugBottomPrice.textContent = config.bottom_price ? this.formatMoney(config.bottom_price) : 'N/A';

      const debugRemarks = document.querySelector('[data-debug-remarks]');
      if (debugRemarks) debugRemarks.textContent = config.remarks || 'None';

      const debugVersion = document.querySelector('[data-debug-version]');
      if (debugVersion) debugVersion.textContent = config.version || 'N/A';

      const debugUpdatedAt = document.querySelector('[data-debug-updated-at]');
      if (debugUpdatedAt) debugUpdatedAt.textContent = config.updated_at || 'N/A';

      const debugAdvancedStoneConfig = document.querySelector('[data-debug-advanced-stone-config]');
      if (debugAdvancedStoneConfig) {
        const stoneConfigCount = config.advanced_stone_config ? config.advanced_stone_config.length : 0;
        debugAdvancedStoneConfig.textContent = stoneConfigCount > 0 ? `${stoneConfigCount} configs` : 'None';
      }

      // Shop Metal Prices
      const debugGold24k = document.querySelector('[data-debug-gold-24k]');
      if (debugGold24k) debugGold24k.textContent = shopData.gold_price_24k ? `Rs. ${shopData.gold_price_24k}/gm` : 'N/A';

      const debugGold22k = document.querySelector('[data-debug-gold-22k]');
      if (debugGold22k) debugGold22k.textContent = shopData.gold_price_22k ? `Rs. ${shopData.gold_price_22k}/gm` : 'N/A';

      const debugGold21k = document.querySelector('[data-debug-gold-21k]');
      if (debugGold21k) debugGold21k.textContent = shopData.gold_price_21k ? `Rs. ${shopData.gold_price_21k}/gm` : 'N/A';

      const debugGold18k = document.querySelector('[data-debug-gold-18k]');
      if (debugGold18k) debugGold18k.textContent = shopData.gold_price_18k ? `Rs. ${shopData.gold_price_18k}/gm` : 'N/A';

      const debugGold14k = document.querySelector('[data-debug-gold-14k]');
      if (debugGold14k) debugGold14k.textContent = shopData.gold_price_14k ? `Rs. ${shopData.gold_price_14k}/gm` : 'N/A';

      const debugGold10k = document.querySelector('[data-debug-gold-10k]');
      if (debugGold10k) debugGold10k.textContent = shopData.gold_price_10k ? `Rs. ${shopData.gold_price_10k}/gm` : 'N/A';

      const debugGold9k = document.querySelector('[data-debug-gold-9k]');
      if (debugGold9k) debugGold9k.textContent = shopData.gold_price_9k ? `Rs. ${shopData.gold_price_9k}/gm` : 'N/A';

      const debugGold8k = document.querySelector('[data-debug-gold-8k]');
      if (debugGold8k) debugGold8k.textContent = shopData.gold_price_8k ? `Rs. ${shopData.gold_price_8k}/gm` : 'N/A';

      const debugSilverPrice = document.querySelector('[data-debug-silver-price]');
      if (debugSilverPrice) debugSilverPrice.textContent = shopData.silver_price ? `Rs. ${shopData.silver_price}/gm` : 'N/A';

      const debugPlatinumPrice = document.querySelector('[data-debug-platinum-price]');
      if (debugPlatinumPrice) debugPlatinumPrice.textContent = shopData.platinum_price ? `Rs. ${shopData.platinum_price}/gm` : 'N/A';

      const debugPalladiumPrice = document.querySelector('[data-debug-palladium-price]');
      if (debugPalladiumPrice) debugPalladiumPrice.textContent = shopData.palladium_price ? `Rs. ${shopData.palladium_price}/gm` : 'N/A';

      const debugIncludeTaxes = document.querySelector('[data-debug-include-taxes]');
      if (debugIncludeTaxes) debugIncludeTaxes.textContent = shopData.include_taxes ? 'Yes' : 'No';

      const debugDefaultTax = document.querySelector('[data-debug-default-tax]');
      if (debugDefaultTax) debugDefaultTax.textContent = shopData.default_tax ? `${shopData.default_tax}%` : '0%';

      const debugMakingChargesDiscount = document.querySelector('[data-debug-making-charges-discount]');
      if (debugMakingChargesDiscount) debugMakingChargesDiscount.textContent = shopData.making_charges_discount ? `${shopData.making_charges_discount}${shopData.making_charges_discount_type === 'percentage' ? '%' : ''}` : 'None';

      const debugDiamondDiscount = document.querySelector('[data-debug-diamond-discount]');
      if (debugDiamondDiscount) debugDiamondDiscount.textContent = shopData.diamond_discount ? `${shopData.diamond_discount}${shopData.diamond_discount_type === 'percentage' ? '%' : ''}` : 'None';

      const debugLastUpdatedOn = document.querySelector('[data-debug-last-updated-on]');
      if (debugLastUpdatedOn) debugLastUpdatedOn.textContent = shopData.last_updated_on || 'N/A';

      const debugShopVersion = document.querySelector('[data-debug-shop-version]');
      if (debugShopVersion) debugShopVersion.textContent = shopData.version || 'N/A';

      // Calculated Values
      const debugSelectedRate = document.querySelector('[data-debug-selected-rate]');
      if (debugSelectedRate) debugSelectedRate.textContent = `Rs. ${calc.goldRate}/gm`;

      const debugGoldTotal = document.querySelector('[data-debug-gold-total]');
      if (debugGoldTotal) debugGoldTotal.textContent = this.formatMoney(calc.goldTotal);

      const debugSubtotal = document.querySelector('[data-debug-subtotal]');
      if (debugSubtotal) debugSubtotal.textContent = this.formatMoney(calc.subtotal);

      const debugTaxAmount = document.querySelector('[data-debug-tax-amount]');
      if (debugTaxAmount) debugTaxAmount.textContent = this.formatMoney(calc.taxAmount);

      const debugTotalPrice = document.querySelector('[data-debug-total-price]');
      if (debugTotalPrice) debugTotalPrice.textContent = this.formatMoney(calc.totalPrice);

      // Variant Options
      const debugMetalColor = document.querySelector('[data-debug-metal-color]');
      if (debugMetalColor) debugMetalColor.textContent = metalColor || 'N/A';

      const debugVariantId = document.querySelector('[data-debug-variant-id]');
      if (debugVariantId) debugVariantId.textContent = variantId;

      // Raw JSON Data
      const debugRawVariant = document.querySelector('[data-debug-raw-variant-json]');
      if (debugRawVariant) {
        debugRawVariant.textContent = JSON.stringify(config, null, 2);
      }

      const debugRawShop = document.querySelector('[data-debug-raw-shop-json]');
      if (debugRawShop) {
        debugRawShop.textContent = JSON.stringify(shopData, null, 2);
      }
    }

    /**
     * Temporarily disable option selector listeners (called by drawer)
     * This prevents conflict when drawer is handling the variant change
     */
    disableOptionSelectorsTemporarily() {
      if (DEBUG_MODE) console.log('🚫 Price Breakup Sync: Temporarily disabling option selector listeners');
      this.optionSelectorsDisabled = true;

      // Re-enable after 500ms (after drawer's update completes)
      setTimeout(() => {
        this.optionSelectorsDisabled = false;
        if (DEBUG_MODE) console.log('✅ Price Breakup Sync: Re-enabled option selector listeners');
      }, 500);
    }

    /**
     * Format money amount
     */
    formatMoney(amount) {
      // Use Shopify's formatMoney if available
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(amount * 100, Shopify.money_format || 'Rs. {{amount}}');
      }

      // Fallback to Indian rupee format
      const formatted = amount.toLocaleString('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      });
      return `Rs. ${formatted}`;
    }
  }

  // Initialize the sync and expose it globally so drawer can trigger updates
  window.priceBreakupSync = new PriceBreakupSync();
  if (DEBUG_MODE) console.log('💎 Price Breakup Sync: Instance exposed to window.priceBreakupSync');

})();
